// English source strings for the operations console stream (ADR-029): site operating hours,
// closures and reception rules (PB-12-01, PB-06-07, PB-06-15), central multi-site changes
// (PB-12-06) and background-job queue figures (PB-19-07).
export const enOpsui = {
  // ------------------------------------------------------------ API messages
  'api.opsuiImpactReview': 'This change affects planned visits. Review the affected visits, then confirm.',
  'api.opsuiImpactStale': 'The affected visits changed since your review. Review them again, then confirm.',
  'api.opsuiClosureRemoved': 'This closure was already removed.',
  'api.opsuiOutcomeUnknown': 'The provider outcome of this job is unknown. Read it back or reconcile it first; replaying could repeat the effect.',
  'v.opsuiImpactToken': 'The impact review reference is not valid. Check the impact again.',
  'v.opsuiFallbackAfterHost': 'The fallback must come after the host reminder.',
  'v.opsuiDuplicateSite': 'Each site can appear only once.',
  'v.opsuiContactNotEligible': 'Choose someone who can currently work at reception on this site.',
  'v.opsuiHours.day': 'Use a day from Monday to Sunday.',
  'v.opsuiHours.format': 'Use 24-hour time, for example 08:30.',
  'v.opsuiHours.order': 'Closing time must be after opening time (00:00 means midnight at the end of the day).',
  'v.opsuiHours.overlap': 'This range overlaps another range on the same day.',
  'v.opsuiClosure.dates': 'Use dates in the form YYYY-MM-DD.',
  'v.opsuiClosure.order': 'The last day must be on or after the first day.',
  'v.opsuiClosure.too_long': 'A closure can cover at most one year.',
  'v.opsuiClosure.hours_required': 'Reduced hours need an opening and a closing time.',
  'v.opsuiClosure.hours_not_allowed': 'Only reduced-hours days have opening times.',
  'v.opsuiClosure.hours_order': 'Closing time must be after opening time.',
  'v.opsuiClosure.overlap': 'These dates overlap another closure of this site.',

  // ------------------------------------------------------------ site tabs
  'opsui.tab.hours': 'Hours & rules',
  'opsui.tab.batch': 'Multi-site changes',
  'opsui.col.action': 'Action',

  // ------------------------------------------------------------ impact review
  'opsui.impact.check': 'Check impact on planned visits',
  'opsui.impact.none': 'No planned visit is affected. Visits already outside the current hours: {already}.',
  'opsui.impact.title': { one: '{count} planned visit would fall outside the hours', other: '{count} planned visits would fall outside the hours' },
  'opsui.impact.body': 'Nothing is cancelled or moved. Visits keep their times; tell the hosts if plans need to change.',
  'opsui.impact.more': { one: 'and {count} more visit', other: 'and {count} more visits' },
  'opsui.impact.truncated': 'There are more future visits than were checked; the count may be higher.',
  'opsui.impact.ack': { one: 'I reviewed the {count} affected visit', other: 'I reviewed the {count} affected visits' },
  'opsui.reason.closure': 'site closed (closure or holiday)',
  'opsui.reason.closed_day': 'site closed that day',
  'opsui.reason.outside_hours': 'outside opening hours',

  // ------------------------------------------------------------ weekly hours
  'opsui.hours.title': 'Opening hours',
  'opsui.hours.sub': 'Local time in {zone}. Daylight-saving changes never move these times.',
  'opsui.hours.unset': 'No opening hours are set, so the site is treated as open at any time.',
  'opsui.hours.day': 'Day',
  'opsui.hours.ranges': 'Open',
  'opsui.hours.closed': 'Closed',
  'opsui.hours.open': 'Opens',
  'opsui.hours.close': 'Closes',
  'opsui.hours.addRange': 'Add hours',
  'opsui.hours.removeRange': 'Remove',
  'opsui.hours.midnight': 'Use 00:00 as the closing time for midnight at the end of the day. For overnight opening, add hours on both days.',
  'opsui.hours.save': 'Save opening hours',
  'opsui.hours.saved': 'Opening hours saved.',

  // ------------------------------------------------------------ closures
  'opsui.closures.title': 'Closures and holidays',
  'opsui.closures.sub': 'Whole local days. Reduced hours replace the weekly hours on those days.',
  'opsui.closures.none': 'No closures or holidays are planned.',
  'opsui.closures.label': 'Name',
  'opsui.closures.kind': 'Type',
  'opsui.closures.dates': 'Dates',
  'opsui.closures.from': 'First day',
  'opsui.closures.to': 'Last day',
  'opsui.closures.localDates': 'Local dates in {zone}.',
  'opsui.closures.add': 'Add closure',
  'opsui.closures.remove': 'Remove',
  'opsui.closures.removeTitle': 'Remove closure',
  'opsui.kind.holiday': 'Public holiday',
  'opsui.kind.closure': 'Site closure',
  'opsui.kind.reduced_hours': 'Reduced hours',

  // ------------------------------------------------------------ reception rules
  'opsui.rules.title': 'Reception follow-up',
  'opsui.rules.sub': 'Who is told when a visitor waits too long, or when no checkout is recorded after a visit ends.',
  'opsui.rules.effective': 'In force now: host reminder after {host} minutes, fallback after {fallback} minutes.',
  'opsui.rules.waitingEnabled': 'Remind the host, then a fallback contact, when a visitor waits too long',
  'opsui.rules.hostMinutes': 'Remind the host after (minutes)',
  'opsui.rules.fallbackMinutes': 'Notify the fallback after (minutes)',
  'opsui.rules.default': 'Leave empty to use the default ({minutes} minutes).',
  'opsui.rules.lead': 'Fallback contact (reception lead)',
  'opsui.rules.leadHint': 'Co-hosts of the visit are also told. Each notice goes once.',
  'opsui.rules.overstayEnabled': 'Tell the host and the site owner when no checkout is recorded after a visit ends',
  'opsui.rules.owner': 'Site owner',
  'opsui.rules.ownerHint': 'Named on overstay follow-up tasks. A notice never says the visitor has left.',
  'opsui.rules.none': 'Nobody',
  'opsui.rules.contactUnavailable': 'No longer available',
  'opsui.rules.save': 'Save follow-up rules',
  'opsui.rules.saved': 'Follow-up rules saved.',

  // ------------------------------------------------------------ escalation ledger
  'opsui.esc.title': 'Recent reception follow-up',
  'opsui.esc.sub': 'Each threshold notifies once. Delivery results never change an arrival or presence.',
  'opsui.esc.none': 'No follow-up was needed recently.',
  'opsui.esc.noAccess': 'Reception follow-up history needs reception access to this site.',
  'opsui.esc.kind': 'Follow-up',
  'opsui.esc.due': 'Threshold reached',
  'opsui.esc.notices': 'Notices',
  'opsui.esc.after': 'after {minutes} min',
  'opsui.esc.pending': 'Waiting to be sent',
  'opsui.esc.k.waiting_host': 'Host reminded of a long wait',
  'opsui.esc.k.waiting_fallback': 'Fallback told of a long wait',
  'opsui.esc.k.overstay': 'No checkout after the visit ended',
  'opsui.msg.queued': 'Queued',
  'opsui.msg.accepted': 'Sent',
  'opsui.msg.failed': 'Failed (will retry)',
  'opsui.msg.blocked': 'Not sent',

  // ------------------------------------------------------------ time-zone review
  'opsui.tz.visits': { zero: 'No planned visit is affected.', one: '{count} planned visit keeps its exact time; local readings change as shown.', other: '{count} planned visits keep their exact times; local readings change as shown.' },

  // ------------------------------------------------------------ multi-site changes
  'opsui.batch.title': 'Multi-site changes',
  'opsui.batch.intro': 'Apply one change to several sites. Each site is checked on its own and gets its own result.',
  'opsui.batch.change': 'Change',
  'opsui.batch.changeSub': 'Choose what to change and which sites to change it on.',
  'opsui.batch.kind': 'What to change',
  'opsui.batch.k.operating_hours': 'Opening hours',
  'opsui.batch.k.operating_rules': 'Reception follow-up rules',
  'opsui.batch.k.branding_colour': 'Brand colour',
  'opsui.batch.colour': 'Brand colour',
  'opsui.batch.colourHint': 'White text must stay readable on it.',
  'opsui.batch.keepBlank': 'Leave empty to keep each site’s value.',
  'opsui.batch.keep': 'Keep each site’s setting',
  'opsui.batch.on': 'On',
  'opsui.batch.off': 'Off',
  'opsui.batch.days': 'Open on',
  'opsui.batch.sites': 'Sites',
  'opsui.batch.preview': 'Preview per site',
  'opsui.batch.previewTitle': 'Preview',
  'opsui.batch.previewSub': 'Sites that are refused here will not be changed.',
  'opsui.batch.site': 'Site',
  'opsui.batch.outcome': 'Result',
  'opsui.batch.impact': 'Planned visits',
  'opsui.batch.version': 'Version',
  'opsui.batch.reasonHint': 'Recorded in the audit trail of every site.',
  'opsui.batch.apply': { one: 'Apply to {count} site', other: 'Apply to {count} sites' },
  'opsui.batch.resultTitle': 'Result per site',
  'opsui.batch.history': 'Recent multi-site changes',
  'opsui.batch.noHistory': 'No multi-site change yet.',
  'opsui.batch.applied': 'Applied to {applied} of {total} sites',
  'opsui.batch.unknownSite': 'Site not available',
  'opsui.batch.o.ready': 'Ready',
  'opsui.batch.o.applied': 'Applied',
  'opsui.batch.o.unchanged': 'Already set',
  'opsui.batch.o.impact_review_required': 'Review planned visits',
  'opsui.batch.o.forbidden': 'Not permitted',
  'opsui.batch.o.not_found': 'Not available',
  'opsui.batch.o.version_conflict': 'Changed meanwhile',
  'opsui.batch.o.invalid': 'Not valid here',
  'opsui.batch.o.failed': 'Failed',
  'opsui.batch.p.hours': 'The hours are not valid.',
  'opsui.batch.p.fallback_before_host': 'The fallback would come before the host reminder.',
  'opsui.batch.p.contrast': 'White text would not be readable on this colour.',
  'opsui.batch.s.completed': 'Completed',
  'opsui.batch.s.partial': 'Partly applied',
  'opsui.batch.s.failed': 'Nothing applied',
  'opsui.batch.s.unchanged': 'Nothing to change',

  // ------------------------------------------------------------ background jobs per topic
  'opsui.topics.title': 'Queues by topic',
  'opsui.topics.sub': 'Work not yet delivered in your scope: how much, and how long the oldest has waited.',
  'opsui.topics.none': 'No undelivered work.',
  'opsui.topics.topic': 'Topic',
  'opsui.topics.due': 'Due now',
  'opsui.topics.retrying': 'Retrying',
  'opsui.topics.scheduled': 'Scheduled',
  'opsui.topics.inFlight': 'In progress',
  'opsui.topics.dead': 'Dead letters',
  'opsui.topics.age': 'Oldest due',
  'opsui.topics.oldestDead': 'oldest {time}',
  'opsui.err.timeout': 'Timeout',
  'opsui.err.unavailable': 'Unavailable',
  'opsui.err.rejected': 'Rejected',
  'opsui.err.invalid': 'Invalid',
  'opsui.err.conflict': 'Conflict',
  'opsui.err.restore_fence': 'Fenced after restore',
  'opsui.err.outcome_unknown': 'Outcome unknown',
  'opsui.err.other': 'Other error',
  'opsui.ext.title': '{kind}: {state}',
  'opsui.ext.k.credential_operation': 'Credential operation',
  'opsui.ext.k.gate_command': 'Barrier command',
  'opsui.ext.s.queued': 'queued',
  'opsui.ext.s.dispatched': 'sent, no answer recorded (a replay marks it unknown and reads it back; it never resends)',
  'opsui.ext.s.acknowledged': 'acknowledged',
  'opsui.ext.s.confirmed': 'confirmed',
  'opsui.ext.s.failed': 'failed',
  'opsui.ext.s.unknown': 'outcome unknown',
  'opsui.ext.s.reconciling': 'being read back',
  'opsui.ext.blocked': 'Replay is blocked until a read-back or reconciliation settles the outcome.',
  'opsui.ext.settled': 'The provider outcome is settled; a replay cannot repeat an unknown effect.',
} as const;
